import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Download,
  QrCode,
  Shield,
  Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import EmergencyAuditLog from "../../components/emergency/EmergencyAuditLog";
import MedicalDevices from "../../components/emergency/MedicalDevices";
import EmergencyValidationPanel from "../../components/emergency/EmergencyValidationPanel";
import { NudgeBanner } from "../../components/ui/NudgeBanner";
import { PageBanner } from "../../components/ui/PageBanner";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { useUnitSystem } from "../../hooks/useUnitSystem";
import {
  CompletenessScore,
  ConsentSettings,
  EmergencyProfileData,
  QRCodeData,
  emergencyService,
} from "../../services/emergency.service";
import { useI18nStore } from "../../stores/i18nStore";
import { downloadEmergencyCardPDF } from "../../utils/emergencyPDFExport";
import { i18nToast } from "../../utils/i18nToast";
import { log } from "@/lib/log";

const EmergencyProfilePage: React.FC = () => {
  const { t, language } = useI18nStore();
  const fmt = useLocaleFormat();
  const navigate = useNavigate();
  const {
    system: unitSystem,
    toggleSystem: toggleUnitSystem,
    formatWeight,
    formatHeight,
  } = useUnitSystem();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"p1" | "p2" | "p3">("p1");
  const [emergencyData, setEmergencyData] =
    useState<EmergencyProfileData | null>(null);
  const [completenessScore, setCompletenessScore] =
    useState<CompletenessScore | null>(null);
  const [validation, setValidation] = useState<
    import("../../services/emergency.service").ValidationResult | null
  >(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [activePanel, setActivePanel] = useState<
    "none" | "audit" | "validation"
  >("none");
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [qrCodeData, setQRCodeData] = useState<QRCodeData | null>(null);
  const [generatingQR, setGeneratingQR] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consent, setConsent] = useState<ConsentSettings>({
    shareAllergies: true,
    shareMedications: true,
    shareConditions: true,
    shareDevices: true,
    shareEmergencyContacts: true,
    shareDemographics: true,
  });

  useEffect(() => {
    loadEmergencyData();
  }, []);

  const loadEmergencyData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load required emergency profile data first
      const profileData = await emergencyService.getEmergencyProfile();
      setEmergencyData(profileData);

      // Load optional sections independently so one failure does not break the page
      const [scoreResult, qrResult, consentResult] = await Promise.allSettled([
        emergencyService.getCompletenessScore(),
        emergencyService.getActiveQRCode(),
        emergencyService.getConsent(),
      ]);

      if (scoreResult.status === "fulfilled") {
        setCompletenessScore(scoreResult.value);
      } else {
        console.error("Failed to load completeness score:", scoreResult.reason);
        setCompletenessScore(null);
      }

      if (qrResult.status === "fulfilled" && qrResult.value) {
        setQRCodeData(qrResult.value);
      }

      if (consentResult.status === "fulfilled") {
        setConsent(consentResult.value);
      } else {
        log.debug("No consent settings found, using defaults");
      }
    } catch (error: any) {
      console.error("Failed to load emergency data:", error);
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.message ||
        t.hrpEpLoadFailed;
      setError(errorMessage);

      // Don't show toast for auth errors
      if (!error?.response || error?.response?.status !== 401) {
        i18nToast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQR = async () => {
    // Open consent dialog first
    setShowConsentDialog(true);
  };

  const handleConsentConfirm = async () => {
    try {
      setGeneratingQR(true);
      setShowConsentDialog(false);

      // Save consent settings
      await emergencyService.saveConsent(consent);

      // Generate QR code (15 minutes default)
      const qrData = await emergencyService.generateQRCode(15, consent);
      setQRCodeData(qrData);
      setShowQRDialog(true);

      i18nToast.success(t.hrpEpQrGenerated);
    } catch (error: any) {
      console.error("Failed to generate QR code:", error);
      i18nToast.error(t.hrpEpQrGenerateFailed);
    } finally {
      setGeneratingQR(false);
    }
  };

  const handleRevokeQR = async () => {
    try {
      await emergencyService.revokeQRCode();
      setQRCodeData(null);
      setShowQRDialog(false);
      i18nToast.success(t.hrpEpQrRevoked);
    } catch (error: any) {
      console.error("Failed to revoke QR code:", error);
      i18nToast.error(t.hrpEpQrRevokeFailed);
    }
  };

  const handleSaveConsent = async () => {
    try {
      await emergencyService.saveConsent(consent);
      i18nToast.success(t.hrpEpConsentSaved);
      // Reload consent to get updated timestamp
      const updatedConsent = await emergencyService.getConsent();
      setConsent(updatedConsent);
    } catch (error: any) {
      console.error("Failed to save consent:", error);
      i18nToast.error(t.hrpEpConsentSaveFailed);
    }
  };

  const handleDownloadPDF = () => {
    if (!emergencyData) {
      i18nToast.error(t.hrpEpNoData);
      return;
    }
    try {
      downloadEmergencyCardPDF(
        {
          patient: {
            name: emergencyData.patient?.name,
            dateOfBirth: emergencyData.patient?.dateOfBirth,
            age: emergencyData.patient?.age,
            gender: emergencyData.patient?.gender,
            bloodType: emergencyData.patient?.bloodType,
            height: emergencyData.patient?.height,
            weight: emergencyData.patient?.weight,
            nationality: emergencyData.patient?.nationality,
          },
          allergies: (emergencyData.allergies || []).map((a: any) => ({
            allergen: a.allergen || a.substance || "",
            criticality: a.criticality || "unable-to-assess",
            reactionType: a.reactionType,
          })),
          medications: (emergencyData.medications || []).map((m: any) => ({
            medicationName: m.medicationName || m.name || "",
            dosage: m.dosage || "",
            frequency: m.frequency,
            route: m.route,
          })),
          emergencyContacts: (emergencyData.emergencyContacts || []).map(
            (c: any) => ({
              name: c.name || "",
              relationship: c.relationship || "",
              phone: c.phone || "",
              email: c.email,
            }),
          ),
          qrCodeDataUrl: qrCodeData?.accessUrl,
          generatedAt: new Date().toISOString(),
        },
        // The app language, all five of them: "de"/"en" alone printed a
        // French, Spanish or Portuguese card in English with US units.
        language,
      );
      i18nToast.success(t.hrpEpPdfDownloaded);
    } catch (error: any) {
      console.error("PDF generation failed:", error);
      i18nToast.error(t.hrpEpPdfFailed);
    }
  };

  const handleRefreshValidation = async () => {
    try {
      setValidationLoading(true);
      const result = await emergencyService.triggerValidation();
      setValidation(result);
    } catch {
      // ignore
    } finally {
      setValidationLoading(false);
    }
  };

  const conditionStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      active: t.hrpEpStatusActive,
      inactive: t.hrpEpStatusInactive,
      resolved: t.hrpEpStatusResolved,
      remission: t.hrpEpStatusRemission,
      recurrence: t.hrpEpStatusRecurrence,
      relapse: t.hrpEpStatusRecurrence,
    };
    return labels[status] ?? status;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreIcon = (score: number) => {
    if (score >= 80) return CheckCircle;
    if (score >= 50) return AlertTriangle;
    return AlertCircle;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <PageBanner
          icon={Shield}
          title={t.myEmergencyProfile}
          description={t.emergencyData}
        />
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t.loading}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Page Banner */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <PageBanner
            icon={Shield}
            title={t.myEmergencyProfile}
            description={t.emergencyData}
          />
        </div>
        <button
          onClick={toggleUnitSystem}
          className="mt-1 px-3 py-1.5 text-xs font-medium rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors shrink-0"
          title={t.hrpEpToggleUnits}
        >
          {unitSystem === "metric"
            ? `🇺🇸 ${t.hrpEpImperial}`
            : `🇪🇺 ${t.hrpEpMetric}`}
        </button>
      </div>
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-6 h-6 text-red-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 mb-2">
                {t.hrpEpLoadFailedTitle}
              </h3>
              <p className="text-sm text-red-800 mb-4">{error}</p>
              <div className="flex space-x-3">
                <button
                  onClick={loadEmergencyData}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                >
                  {t.hrpEpRetry}
                </button>
                <button
                  onClick={() => navigate("/traveler/health")}
                  className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 text-sm"
                >
                  {t.hrpEpGoToHealthRecords}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Completeness Score Widget */}
      {completenessScore && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {(() => {
                const ScoreIcon = getScoreIcon(completenessScore.score);
                return (
                  <ScoreIcon
                    className={`w-12 h-12 ${getScoreColor(completenessScore.score)}`}
                  />
                );
              })()}
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t.completenessScore}
                </h3>
                <p className="text-sm text-gray-600">
                  {completenessScore.completedFields} /{" "}
                  {completenessScore.totalFields} {t.criticalData}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div
                className={`text-4xl font-bold ${getScoreColor(completenessScore.score)}`}
              >
                {completenessScore.score}%
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {completenessScore.score >= 80
                  ? t.dataQualityGood
                  : completenessScore.score >= 50
                    ? t.dataQualityFair
                    : t.dataQualityPoor}
              </p>
            </div>
          </div>

          {/* Missing Data Warning */}
          {completenessScore.missingCritical &&
            completenessScore.missingCritical.length > 0 && (
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-yellow-800">
                      {t.missingCriticalData}
                    </h4>
                    <ul className="mt-2 space-y-1">
                      {completenessScore.missingCritical.map((item, idx) => (
                        <li key={idx} className="text-sm text-yellow-700">
                          • {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <button
          onClick={handleGenerateQR}
          disabled={generatingQR}
          className="p-6 bg-white rounded-lg shadow-sm border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <QrCode className="w-8 h-8 text-primary-600 mb-3" />
          <h3 className="font-semibold text-gray-900">
            {qrCodeData ? t.hrpEpViewQr : t.emergencyQRCode}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {qrCodeData
              ? t.hrpEpActiveUntil.replace(
                  "{time}",
                  fmt.time(qrCodeData.expiresAt),
                )
              : t.shareEmergencyAccess}
          </p>
          {generatingQR && (
            <div className="mt-2">
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
            </div>
          )}
        </button>

        <button
          onClick={handleDownloadPDF}
          disabled={!emergencyData}
          className="p-6 bg-white rounded-lg shadow-sm border border-gray-200 hover:border-green-300 hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-8 h-8 text-green-600 mb-3" />
          <h3 className="font-semibold text-gray-900">{t.hrpEpDownloadCard}</h3>
          <p className="text-sm text-gray-600 mt-1">{t.hrpEpPrintablePdf}</p>
        </button>

        <button
          onClick={() => alert(t.hrpEpContactsComingSoon)}
          className="p-6 bg-white rounded-lg shadow-sm border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all"
        >
          <Users className="w-8 h-8 text-primary-600 mb-3" />
          <h3 className="font-semibold text-gray-900">{t.emergencyContacts}</h3>
          <p className="text-sm text-gray-600 mt-1">{t.addEmergencyContact}</p>
        </button>

        <button
          onClick={() => navigate("/traveler/health")}
          className="p-6 bg-white rounded-lg shadow-sm border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all"
        >
          <Activity className="w-8 h-8 text-primary-600 mb-3" />
          <h3 className="font-semibold text-gray-900">{t.editEmergencyData}</h3>
          <p className="text-sm text-gray-600 mt-1">{t.priority1Data}</p>
        </button>
      </div>

      {/* Priority Data Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav
            className="-mb-px flex space-x-8 px-6"
            aria-label={t.hrpEpPriorityTabs}
          >
            <button
              onClick={() => setActiveTab("p1")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "p1"
                  ? "border-red-500 text-red-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {t.priority1Data}
              <span
                className={`ml-2 py-1 px-2 rounded-full text-xs ${
                  activeTab === "p1"
                    ? "bg-red-100 text-red-700"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {(emergencyData?.allergies?.length || 0) +
                  (emergencyData?.medications?.length || 0) +
                  (emergencyData?.conditions?.length || 0) +
                  (emergencyData?.emergencyContacts?.length || 0)}
              </span>
            </button>

            <button
              onClick={() => setActiveTab("p2")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "p2"
                  ? "border-yellow-500 text-yellow-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {t.priority2Data}
              <span
                className={`ml-2 py-1 px-2 rounded-full text-xs ${
                  activeTab === "p2"
                    ? "bg-yellow-100 text-yellow-700"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {(emergencyData?.devices?.length || 0) +
                  (emergencyData?.patient?.bloodType ? 1 : 0) +
                  (emergencyData?.patient?.height ? 1 : 0)}
              </span>
            </button>

            <button
              onClick={() => setActiveTab("p3")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "p3"
                  ? "border-green-500 text-green-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {t.priority3Data}
              <span
                className={`ml-2 py-1 px-2 rounded-full text-xs ${
                  activeTab === "p3"
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                0
              </span>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === "p1" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t.criticalData} - {t.priority1Data}
                </h3>
                <p className="text-sm text-gray-600 mb-6">{t.hrpEpP1Intro}</p>

                {/* P1 Data Sections - Read Only Display */}
                <div className="space-y-4">
                  {/* Allergies */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">
                        {t.hrpEpAllergies}
                      </h4>
                      <button
                        onClick={() => navigate("/traveler/health")}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        → {t.hrpEpEditInHealthRecords}
                      </button>
                    </div>
                    {emergencyData?.allergies?.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">
                        {t.hrpEpNoAllergies}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {emergencyData?.allergies?.map((allergy, idx) => (
                          <div key={idx} className="text-sm text-gray-700">
                            • {allergy.allergen} -{" "}
                            {allergy.criticality === "high"
                              ? t.hrpEpCriticalityHigh
                              : allergy.criticality === "low"
                                ? t.hrpEpCriticalityLow
                                : t.hrpEpCriticalityUnknown}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Medications */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">
                        {t.medications}
                      </h4>
                      <button
                        onClick={() => navigate("/traveler/health")}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        → {t.hrpEpEditInHealthRecords}
                      </button>
                    </div>
                    {emergencyData?.medications?.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">
                        {t.hrpEpNoMedications}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {emergencyData?.medications?.map((med, idx) => (
                          <div key={idx} className="text-sm text-gray-700">
                            • {med.medicationName} - {med.dosage}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Conditions */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">
                        {t.hrpEpConditions}
                      </h4>
                      <button
                        onClick={() => navigate("/traveler/health")}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        → {t.hrpEpEditInHealthRecords}
                      </button>
                    </div>
                    {emergencyData?.conditions?.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">
                        {t.hrpEpNoConditions}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {emergencyData?.conditions?.map((condition, idx) => (
                          <div key={idx} className="text-sm text-gray-700">
                            • {condition.condition} -{" "}
                            {conditionStatusLabel(condition.clinicalStatus)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Emergency Contacts */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">
                        {t.emergencyContacts}
                      </h4>
                      <button
                        onClick={() => navigate("/traveler/health")}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        → {t.hrpEpEditInHealthRecords}
                      </button>
                    </div>
                    {emergencyData?.emergencyContacts?.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">
                        {t.hrpEpNoContacts}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {emergencyData?.emergencyContacts?.map(
                          (contact, idx) => (
                            <div key={idx} className="text-sm text-gray-700">
                              • {contact.name} - {contact.relationship} -{" "}
                              {contact.phone}
                            </div>
                          ),
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "p2" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t.priority2Data}
                </h3>
                <p className="text-sm text-gray-600 mb-6">{t.hrpEpP2Intro}</p>

                <div className="space-y-4">
                  {/* Blood Type */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">
                      {t.bloodType}
                    </h4>
                    {emergencyData?.patient?.bloodType ? (
                      <p className="text-sm text-gray-700">
                        {emergencyData.patient.bloodType}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">
                        {t.hrpEpNotRecordedYet}
                      </p>
                    )}
                  </div>

                  {/* Medical Devices.
                      Edited here rather than linked away to Health Records,
                      which has no device editor - that link was the reason this
                      section could never contain anything. */}
                  <MedicalDevices onChange={loadEmergencyData} />

                  {/* Immunizations */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">
                        {t.immunizations}
                      </h4>
                      <button
                        onClick={() => navigate("/traveler/health")}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        → {t.hrpEpEditInHealthRecords}
                      </button>
                    </div>
                    <p className="text-sm text-gray-500 italic">
                      {t.hrpEpNoImmunizations}
                    </p>
                  </div>

                  {/* Height/Weight */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">
                      {t.height} / {t.weight}
                    </h4>
                    {emergencyData?.patient?.height ||
                    emergencyData?.patient?.weight ? (
                      <p className="text-sm text-gray-700">
                        {formatHeight(emergencyData?.patient?.height)} /{" "}
                        {formatWeight(emergencyData?.patient?.weight)}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">
                        {t.hrpEpNotRecordedYet}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "p3" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t.priority3Data}
                </h3>
                <p className="text-sm text-gray-600 mb-6">{t.hrpEpP3Intro}</p>

                <div className="space-y-4">
                  {/* Medical Procedures */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">
                        {t.medicalProcedures}
                      </h4>
                      <button
                        onClick={() => navigate("/traveler/health")}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        → {t.hrpEpEditInHealthRecords}
                      </button>
                    </div>
                    <p className="text-sm text-gray-500 italic">
                      {t.hrpEpNoProcedures}
                    </p>
                  </div>

                  {/* Observations */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">
                        {t.hrpEpClinicalObservations}
                      </h4>
                      <button
                        onClick={() => navigate("/traveler/health")}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        → {t.hrpEpEditInHealthRecords}
                      </button>
                    </div>
                    <p className="text-sm text-gray-500 italic">
                      {t.hrpEpNoObservations}
                    </p>
                  </div>

                  {/* Travel History */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">
                        {t.hrpEpTravelHistory}
                      </h4>
                      <button
                        onClick={() => navigate("/traveler/health")}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        → {t.hrpEpEditInHealthRecords}
                      </button>
                    </div>
                    <p className="text-sm text-gray-500 italic">
                      {t.hrpEpNoTravelHistory}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Emergency Data Sharing Consent */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start space-x-4">
          <Shield className="w-6 h-6 text-primary-600 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t.hrpEpConsentTitle}
            </h3>
            <p className="text-sm text-gray-600 mb-4">{t.hrpEpConsentIntro}</p>

            {/* Consent Checkboxes */}
            <div className="space-y-3">
              <label
                className="flex items-start space-x-3 cursor-pointer"
                aria-label={t.hrpEpShareP1}
              >
                <input
                  type="checkbox"
                  defaultChecked
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  aria-label={t.hrpEpShareP1}
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {t.hrpEpShareP1}
                  </p>
                  <p className="text-xs text-gray-500">{t.hrpEpShareP1Items}</p>
                </div>
              </label>

              <label
                className="flex items-start space-x-3 cursor-pointer"
                aria-label={t.hrpEpShareP2}
              >
                <input
                  type="checkbox"
                  defaultChecked
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  aria-label={t.hrpEpShareP2}
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {t.hrpEpShareP2}
                  </p>
                  <p className="text-xs text-gray-500">{t.hrpEpShareP2Items}</p>
                </div>
              </label>

              <label
                className="flex items-start space-x-3 cursor-pointer"
                aria-label={t.hrpEpShareP3}
              >
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  aria-label={t.hrpEpShareP3}
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {t.hrpEpShareP3}
                  </p>
                  <p className="text-xs text-gray-500">{t.hrpEpShareP3Items}</p>
                </div>
              </label>
            </div>

            {/* Consent Agreement */}
            <div className="mt-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
              <label
                className="flex items-start space-x-3 cursor-pointer"
                aria-label={t.hrpEpConsentAgree}
              >
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-primary-300 rounded"
                  aria-label={t.hrpEpConsentAgree}
                />
                <div>
                  <p className="text-sm font-medium text-primary-900">
                    {t.hrpEpConsentAgreeShort}
                  </p>
                  <p className="text-xs text-primary-700 mt-1">
                    {t.hrpEpConsentAgreeText}
                  </p>
                </div>
              </label>
            </div>

            {/* Save Button */}
            <div className="mt-6 flex items-center justify-between">
              <button
                onClick={handleSaveConsent}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors"
              >
                {t.hrpEpSaveConsent}
              </button>
              <p className="text-xs text-gray-500">
                {t.lastUpdated}:{" "}
                {consent.consentGivenAt
                  ? fmt.dateTime(consent.consentGivenAt)
                  : t.never}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Priority Data Summary - OLD VERSION - Can be removed later */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
        style={{ display: "none" }}
      >
        {/* Priority 1: Critical Data */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t.priority1Data}
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">{t.hrpEpAllergies}</span>
              <span className="text-sm font-medium text-gray-900">
                {emergencyData?.allergies?.length || 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">{t.medications}</span>
              <span className="text-sm font-medium text-gray-900">
                {emergencyData?.medications?.length || 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">{t.hrpEpConditions}</span>
              <span className="text-sm font-medium text-gray-900">
                {emergencyData?.conditions?.length || 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-600">
                {t.emergencyContacts}
              </span>
              <span className="text-sm font-medium text-gray-900">
                {emergencyData?.emergencyContacts?.length || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Priority 2: Important Data */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t.priority2Data}
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">{t.bloodType}</span>
              <span className="text-sm font-medium text-gray-900">{"-"}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">{t.devices}</span>
              <span className="text-sm font-medium text-gray-900">
                {emergencyData?.devices?.length || 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">{t.immunizations}</span>
              <span className="text-sm font-medium text-gray-900">0</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-600">{t.height}</span>
              <span className="text-sm font-medium text-gray-900">{"-"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <NudgeBanner
        variant="info"
        live={false}
        icon={<Shield className="w-5 h-5" />}
        title={
          <>
            {t.emergencyData} - {t.dataQuality}
          </>
        }
      >
        {t.hrpEpKeepUpToDate}
      </NudgeBanner>

      {/* Expandable: Validation & Audit panels */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() =>
              setActivePanel(
                activePanel === "validation" ? "none" : "validation",
              )
            }
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activePanel === "validation"
                ? "bg-primary-50 text-primary-700 border-b-2 border-primary-600"
                : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            🛡 {t.hrpEpProfileValidation}
            {completenessScore && (
              <span
                className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                  completenessScore.score >= 80
                    ? "bg-green-100 text-green-700"
                    : completenessScore.score >= 50
                      ? "bg-yellow-100 text-yellow-700"
                      : "bg-red-100 text-red-700"
                }`}
              >
                {completenessScore.score}%
              </span>
            )}
          </button>
          <button
            onClick={() =>
              setActivePanel(activePanel === "audit" ? "none" : "audit")
            }
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activePanel === "audit"
                ? "bg-orange-50 text-orange-700 border-b-2 border-orange-500"
                : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            📋 {t.hrpEpAccessAuditLog}
          </button>
        </div>
        {activePanel !== "none" && (
          <div className="p-6">
            {activePanel === "validation" && (
              <EmergencyValidationPanel
                validation={validation}
                loading={validationLoading}
                onRefresh={handleRefreshValidation}
              />
            )}
            {activePanel === "audit" && <EmergencyAuditLog />}
          </div>
        )}
      </div>

      {/* Current Consent Status */}
      {consent.consentGivenAt && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-green-900">
                  {t.hrpEpConsentActive}
                </h4>
                <p className="text-xs text-green-700 mt-1">
                  {t.hrpEpConsentGivenOn.replace(
                    "{date}",
                    fmt.date(consent.consentGivenAt),
                  )}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {consent.shareAllergies && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                      ✓ {t.hrpEpAllergies}
                    </span>
                  )}
                  {consent.shareMedications && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                      ✓ {t.medications}
                    </span>
                  )}
                  {consent.shareConditions && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                      ✓ {t.hrpEpConditions}
                    </span>
                  )}
                  {consent.shareEmergencyContacts && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                      ✓ {t.emergencyContacts}
                    </span>
                  )}
                  {consent.shareDevices && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                      ✓ {t.devices}
                    </span>
                  )}
                  {consent.shareDemographics && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                      ✓ {t.hrpEpDemographics}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowConsentDialog(true)}
              className="text-sm text-green-700 hover:text-green-900 underline"
            >
              {t.edit}
            </button>
          </div>
        </div>
      )}

      {/* Consent Dialog */}
      {showConsentDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {t.hrpEpConsentTitle}
              </h3>
              <button
                onClick={() => setShowConsentDialog(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label={t.close}
              >
                ✕
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-6">
              {t.hrpEpConsentDialogIntro}
            </p>

            {/* Consent Options */}
            <div className="space-y-4 mb-6">
              {/* Priority 1 - Critical */}
              <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                <h4 className="font-medium text-red-900 mb-3 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  {t.hrpEpP1Heading}
                </h4>
                <div className="space-y-2">
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={consent.shareAllergies}
                      onChange={(e) =>
                        setConsent({
                          ...consent,
                          shareAllergies: e.target.checked,
                        })
                      }
                      className="mr-2"
                    />
                    <span className="text-gray-700">{t.hrpEpAllergies}</span>
                  </label>
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={consent.shareMedications}
                      onChange={(e) =>
                        setConsent({
                          ...consent,
                          shareMedications: e.target.checked,
                        })
                      }
                      className="mr-2"
                    />
                    <span className="text-gray-700">{t.medications}</span>
                  </label>
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={consent.shareConditions}
                      onChange={(e) =>
                        setConsent({
                          ...consent,
                          shareConditions: e.target.checked,
                        })
                      }
                      className="mr-2"
                    />
                    <span className="text-gray-700">{t.medicalConditions}</span>
                  </label>
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={consent.shareEmergencyContacts}
                      onChange={(e) =>
                        setConsent({
                          ...consent,
                          shareEmergencyContacts: e.target.checked,
                        })
                      }
                      className="mr-2"
                    />
                    <span className="text-gray-700">{t.emergencyContacts}</span>
                  </label>
                </div>
              </div>

              {/* Priority 2 - Important */}
              <div className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
                <h4 className="font-medium text-yellow-900 mb-3">
                  {t.hrpEpP2Heading}
                </h4>
                <div className="space-y-2">
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={consent.shareDevices}
                      onChange={(e) =>
                        setConsent({
                          ...consent,
                          shareDevices: e.target.checked,
                        })
                      }
                      className="mr-2"
                    />
                    <span className="text-gray-700">
                      {t.hrpEpMedicalDevices}
                    </span>
                  </label>
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={consent.shareDemographics}
                      onChange={(e) =>
                        setConsent({
                          ...consent,
                          shareDemographics: e.target.checked,
                        })
                      }
                      className="mr-2"
                    />
                    <span className="text-gray-700">
                      {t.hrpEpDemographicsDetail}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-yellow-800">
                <strong>{t.note}:</strong> {t.hrpEpConsentNote}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConsentDialog(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleConsentConfirm}
                disabled={generatingQR}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generatingQR ? t.generating : t.hrpEpConfirmGenerate}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Dialog */}
      {showQRDialog && qrCodeData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {t.emergencyQRCode}
              </h3>
              <button
                onClick={() => setShowQRDialog(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label={t.close}
              >
                ✕
              </button>
            </div>

            {/* QR Code Display */}
            <div className="bg-gray-50 rounded-lg p-6 flex items-center justify-center mb-4">
              <img
                src={qrCodeData.qrCodeUrl}
                alt={t.emergencyQRCode}
                className="w-64 h-64"
              />
            </div>

            {/* Expiry Info */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>{t.hrpEpExpires}:</strong>{" "}
                {fmt.dateTime(qrCodeData.expiresAt)}
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                {t.hrpEpExpiryNote}
              </p>
            </div>

            {/* Access URL */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.copyAccessLink}
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={qrCodeData.accessUrl}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(qrCodeData.accessUrl);
                    i18nToast.success(t.hrpEpLinkCopied);
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                >
                  {t.copy}
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={() => setShowQRDialog(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                {t.close}
              </button>
              <button
                onClick={handleRevokeQR}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                {t.hrpEpRevokeAccess}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmergencyProfilePage;
